import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { passengerCMSApi, post } from "~/Services";
import { passengerHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import { renderActionsColumn, renderDateColumn, renderPublishedColumn } from "~/Utils/TableRenderFunction";
import { useMainHeaderDocumentDetails } from "./MainHeaderPas.hooks";

export const MainHeaderPassenger = () => {
  // 🔹 Hooks & Utils
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  // 🔹 State Management
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [ignoreSelectionForDrawer, setIgnoreSelectionForDrawer] = useState(false);
  const [id, setId] = useState<string | undefined>();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  // Keep `id` in-sync when a row is selected (e.g. via the eye icon)
  useEffect(() => {
    if (selectedRowData) {
      // If a selection was made for a non-drawer action (e.g. delete), skip opening drawer
      if (ignoreSelectionForDrawer) {
        setIgnoreSelectionForDrawer(false);
        return;
      }
      const newId =
        selectedRowData.id ?? selectedRowData?.payload?.id ?? selectedRowData?.approvalId ?? selectedRowData?._id;
      setId(newId);
      // ensure drawer opens when a row is selected programmatically
      setDrawerVisible(true);
    }
  }, [selectedRowData]);

  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedPublished, setSelectedPublished] = useState<boolean | "">("");
  const [selectedCategoriesData, setSelectedCategoriesData] = useState<any>("");

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [data, setData] = useState<any[]>([]);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");
  const [currentAirport] = useLocalStorage("currentAirport", null);

  const publishedData: TypeData[] = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const { data: drilldown, refetch: refetchDocument } = useMainHeaderDocumentDetails(id || "");

  useEffect(() => {
    if (id) {
      console.log("🔍 Selected ID:", id);
      refetchDocument();
    }
  }, [id]);

  const getRecords = async () => {
    setIsLoading(true);
    try {
      const url = `/api/get-main-menu`;
      const res = await passengerCMSApi.get(url);
      const items = res?.data;

      setData(items);
      setSortedData(items);
      setTotalRecords(items.length);

      console.log("✅ Records fetched:", items);
    } catch (err) {
      console.error("❌ Failed to fetch header records:", err);
      setData([]);
      setSortedData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRecords();
  }, []);

  // useEffect(() => {
  //   let filtered = [...data];

  //   if (searchValue) {
  //     const searchLower = decodeHTML(searchValue).toLowerCase();
  //     filtered = filtered.filter((item: any) =>
  //       decodeHTML(item.title || "").toLowerCase().includes(searchLower)
  //     );
  //   }

  //   if (selectedPublished !== "") {
  //     filtered = filtered.filter(
  //       (item: any) => item.published === selectedPublished
  //     );
  //   }

  //   if (selectedCategoriesData !== "") {
  //     filtered = filtered.filter(
  //       (item: any) =>
  //         decodeHTML(item.field_category_id) === selectedCategoriesData
  //     );
  //   }

  //   setSortedData(filtered);
  //   setTotalRecords(filtered.length);
  //   setCurrentPage(0);
  //   console.log("🔎 Filter applied:", {
  //     searchValue,
  //     selectedPublished,
  //     selectedCategoriesData,
  //   });
  // }, [data, searchValue, selectedPublished, selectedCategoriesData]);

  useEffect(() => {
    let filteredData = Array.isArray(data) ? [...data] : [];

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filteredData = filteredData.filter((item: any) =>
        String(item.name || "")
          .toLowerCase()
          .includes(searchLower)
      );
    }

    if (selectedPublished !== "") {
      filteredData = filteredData.filter((item: any) => item.published === selectedPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);
  useEffect(() => {
    if (query.get("activeTab") === "mainheaders") {
      setSelectedPublished("");
      setSelectedCategoriesData("");
      setSearchValue("");
      setCurrentPage(0);
    }
  }, [query.get("activeTab")]);

  const columns: TableColumnsType<any> = [
    {
      title: "Title",
      dataIndex: "name",
      key: "name",
      render: decodeHTML,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "name"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "name"
            ? "descend"
            : undefined,
    },
    //  {
    //   title: "Banner Image",
    //   dataIndex: "image_url",
    //   key: "image_url",
    //   render: renderImageColumn,
    // },
    // {
    //   title: "Description",
    //   dataIndex: "description",
    //   key: "description",
    //   render: decodeHTML,

    // },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "changed",
      key: "changed",
      // render: renderDateColumn,
      render: (value: any, record: any) => {
        const created = record?.created ? moment(record.created).format("YYYY-MM-DD HH:mm:ss") : null;
        const updated = value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : null;

        if (created && updated && created === updated) {
          return null;
        }

        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "changed"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "changed"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            console.log("✏️ Edit clicked for:", record.id);
            navigate(`/dedicated/passenger/navigation/header/main/add?editId=${record.id}`);
          },
          undefined,
          () => {
            console.log("🗑 Delete clicked for:", record.id);
            // avoid opening the detail drawer when invoking delete
            setIgnoreSelectionForDrawer(true);
            setSelectedRowData(record);
            setDeleteModalVisible(true);
          },
          "users"
        ),
    },
  ];

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (sortOrder === "asc" && orderByName === column.dataIndex) {
      setSortOrder("desc");
      order = "desc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);
    const tempData = [...sortedData];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (!apiUrl) return;
    console.log("📤 Submitting request:", { data, apiUrl, action, method });

    const payload = {
      action,
      method,
      module: "Passenger Navigation",
      subModule: "passengerHeader_mainheaders",
      type: "DEDICATED",
      application: "PASSENGER_ECMS",
      airportName: currentAirport,
      reviewNotes,
      appList: [{ appName: "PASSENGER_ECMS", apiUrl }],
      payload: data,
      status,
    };

    try {
      const response: any = await post("/management/publish", payload);
      openToast({ content: "Data sent for review", type: "success" });

      if (response.status === "OK" && status === "PENDING") {
        navigate("/dedicated/status-information?activeTab=pending");
      }
    } catch {
      openToast({ content: "Failed to send data", type: "error" });
    }
  };

  async function sendDeleteRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${passengerHost}/api/get-main-menu-list-crud-api-data/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {
      console.error("❌ Delete request failed:", err);
    }
  }

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  const handleCategoriesChange = (value: any) => {
    setSelectedCategoriesData(value);
    setCurrentPage(0);
  };

  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={currentPageData || []}
        exportFileName="MAIN_HEADER"
        isLoading={isLoading}
        isPagination
        recordsPerPage={10}
        onSearchChange={(text: string) => {
          setSearchValue(text);
          setCurrentPage(0);
        }}
        hideExport
        searchPlaceholderText="Search Title"
        onClick={(record) => setSelectedRowData(record)}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />

      <div className="pagination">
        <Pagination
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(page, newSize) => {
            setCurrentPage(page - 1);
            if (newSize !== pageSize) {
              setPageSize(newSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <TableDrawer
        title={<Typography.Title level={4}>Detailed View</Typography.Title>}
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setId(undefined);
          setSelectedRowData(null);
        }}
        fields={drilldownData[_get(drilldown, "subModule", "mainheaders") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteRequest}
        defaultReviewNotes="Kindly review this Header"
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string | undefined, sortBy: string) {
  if (["title"].includes(sortBy)) {
    const A = a[sortBy]?.toLowerCase() || "";
    const B = b[sortBy]?.toLowerCase() || "";
    if (order === "desc") return A < B ? 1 : -1;
    if (order === "asc") return A > B ? 1 : -1;
    return 0;
  }

  if (order === "desc") return a[sortBy] < b[sortBy] ? 1 : -1;
  if (order === "asc") return a[sortBy] > b[sortBy] ? 1 : -1;
  return 0;
}

export default MainHeaderPassenger;
